import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Siren, X, CheckCircle } from "@phosphor-icons/react";

/**
 * Global Escalation Bar
 * ---------------------
 * Sits at the very top of every AdminShell / WorkerShell page. Polls
 * `/api/inbox/escalations/live` every 30 seconds and:
 *
 *   1. Renders a red bar if there are any UNOWNED escalations. The bar
 *      persists across every route so office staff can't accidentally
 *      miss one by never visiting /admin/inbox.
 *   2. Fires a sonner toast + a Web Audio "chime" when a NEW unowned
 *      escalation appears mid-session (id we hadn't seen last poll).
 *   3. Provides a one-click "I've got it" button that atomically claims
 *      the newest item — winner takes ownership, banner clears for
 *      everyone else in the workspace.
 *
 * The Web Audio chime is generated on-the-fly (two-note beep) so we
 * don't have to ship an audio asset — one less thing to break in prod.
 * Sound is off until the user makes ANY click on the page (browsers
 * block autoplay until user gesture). Once unlocked, every subsequent
 * new-escalation event chimes automatically.
 */
export default function GlobalEscalationBar() {
  const [snap, setSnap] = useState(null); // {unowned_count, oldest_hours, latest_id, latest}
  const [claiming, setClaiming] = useState(false);
  const lastLatestIdRef = useRef("");     // for new-id detection across polls
  const soundUnlockedRef = useRef(false); // browsers require gesture first
  const audioCtxRef = useRef(null);

  // Cross-browser AudioContext lazy init. We build it on the first user
  // gesture so autoplay policy doesn't kill us.
  const unlockAudio = () => {
    if (soundUnlockedRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtxRef.current = new Ctx();
      soundUnlockedRef.current = true;
    } catch { /* browser said no — fine, banner still works */ }
  };
  useEffect(() => {
    const arm = () => unlockAudio();
    window.addEventListener("click", arm, { once: true });
    window.addEventListener("keydown", arm, { once: true });
    return () => {
      window.removeEventListener("click", arm);
      window.removeEventListener("keydown", arm);
    };
  }, []);

  // Two-tone "alert" — 880Hz then 660Hz, 120ms each. Bright but short
  // so it doesn't feel like a fire alarm every 30 seconds.
  const playChime = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      [{ f: 880, t: now }, { f: 660, t: now + 0.13 }].forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.13);
      });
    } catch { /* audio context ded — skip silently */ }
  };

  const poll = async () => {
    try {
      const { data } = await api.get("/inbox/escalations/live");
      setSnap(data);

      // Detect a brand-new escalation across polls. First load (empty
      // ref) doesn't chime — we don't want a chime on every page load
      // for pre-existing items.
      const newId = data.latest_id || "";
      const prev = lastLatestIdRef.current;
      if (prev && newId && newId !== prev && data.unowned_count > 0) {
        const l = data.latest || {};
        toast.error(
          `🚨 New escalation: ${l.property_address || "unknown address"}`,
          {
            description: `${l.reason_label || l.reason || "Escalated"}${l.worker_name ? ` · ${l.worker_name}` : ""}`,
            duration: 15000,
            action: { label: "Open", onClick: () => { window.location.href = "/admin/inbox"; } },
          }
        );
        playChime();
      }
      // Always advance the pointer so a later transition detects properly.
      if (newId) lastLatestIdRef.current = newId;
      else if (!data.unowned_count) lastLatestIdRef.current = ""; // pool empty
    } catch { /* silent — poller retries in 30s */ }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claim = async () => {
    if (!snap?.latest?.id || claiming) return;
    setClaiming(true);
    try {
      const { data } = await api.post(`/inbox/escalations/${snap.latest.id}/claim`);
      if (data.claimed) {
        toast.success("You've got it — nice grab", {
          description: `${snap.latest.property_address} · ${snap.latest.reason_label || snap.latest.reason || ""}`,
        });
      } else {
        toast.info(`${data.owned_by_name || "Someone else"} already grabbed it`);
      }
      poll(); // immediate refresh — collapses the banner if pool is empty
    } catch (e) {
      toast.error("Could not claim — try again");
    } finally { setClaiming(false); }
  };

  if (!snap || snap.unowned_count === 0) return null;

  const l = snap.latest || {};
  const oldestBadge = snap.oldest_hours >= 1
    ? `oldest ${snap.oldest_hours.toFixed(0)}h ago`
    : `oldest ${Math.max(1, Math.round(snap.oldest_hours * 60))}m ago`;

  return (
    <div
      data-testid="global-escalation-bar"
      className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white shadow-lg shadow-red-500/40 animate-pulse-slow border-b-4 border-red-900"
      style={{ animation: "pulseRed 2.4s ease-in-out infinite" }}
    >
      <style>{`
        @keyframes pulseRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
          50%      { box-shadow: 0 0 24px 4px rgba(239, 68, 68, 0.8); }
        }
      `}</style>
      <div className="max-w-none px-3 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
        <Siren weight="fill" size={22} className="text-white flex-none drop-shadow"/>
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase tracking-widest text-[11px] sm:text-xs leading-tight">
            {snap.unowned_count} escalation{snap.unowned_count === 1 ? "" : "s"} need attention · {oldestBadge}
          </div>
          <div className="text-[11px] sm:text-xs text-red-100 truncate mt-0.5">
            <span className="font-bold">{l.property_address || "—"}</span>
            {l.reason_label && <span className="ml-1">· {l.reason_label}</span>}
            {l.worker_name && <span className="ml-1 opacity-90">· from {l.worker_name}</span>}
          </div>
        </div>
        <button
          onClick={claim}
          disabled={claiming}
          data-testid="escalation-bar-claim"
          className="h-8 px-3 bg-white text-red-700 hover:bg-red-50 font-black uppercase text-[10px] tracking-widest rounded shadow inline-flex items-center gap-1 disabled:opacity-60"
        >
          <CheckCircle weight="fill" size={13}/>
          {claiming ? "Claiming…" : "I've got it"}
        </button>
        <Link
          to="/admin/inbox"
          data-testid="escalation-bar-open-inbox"
          className="h-8 px-3 border-2 border-white/70 hover:bg-white/10 text-white font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center"
        >
          Open Inbox
        </Link>
        {snap.mine_count > 0 && (
          <span
            data-testid="escalation-bar-mine-count"
            className="text-[10px] font-black uppercase tracking-widest bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded"
            title="Escalations you've claimed but haven't finished yet"
          >
            {snap.mine_count} on you
          </span>
        )}
      </div>
    </div>
  );
}
